import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase, ThemeMode } from '../lib/supabase';
import { logger } from '../lib/logger';

// Default menu items configuration
export const DEFAULT_MENU_ORDER = [
  'dashboard',
  'site-pages',
  'forms',
  'leads',
  'todos',
  'calendar',
  'products',
  'analytics',
  'media',
  'social-media',
];

// Color presets for the color picker
export const COLOR_PRESETS = [
  { name: 'Violett', value: '#9B6BFF' },
  { name: 'Blau', value: '#3B82F6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Grün', value: '#10B981' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Rosa', value: '#EC4899' },
  { name: 'Rot', value: '#EF4444' },
  { name: 'Gelb', value: '#EAB308' },
];

// Helper to convert hex to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

// Helper to generate lighter/darker variants
function adjustColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const adjust = (value: number) => {
    const adjusted = Math.round(value + (255 - value) * (percent / 100));
    return Math.min(255, Math.max(0, adjusted));
  };

  if (percent > 0) {
    // Lighten
    return `rgb(${adjust(rgb.r)}, ${adjust(rgb.g)}, ${adjust(rgb.b)})`;
  } else {
    // Darken
    const factor = 1 + percent / 100;
    return `rgb(${Math.round(rgb.r * factor)}, ${Math.round(rgb.g * factor)}, ${Math.round(rgb.b * factor)})`;
  }
}

interface SettingsContextType {
  primaryColor: string;
  themeMode: ThemeMode;
  menuOrder: string[];
  hiddenMenuItems: string[];
  isLoading: boolean;
  setPrimaryColor: (color: string) => Promise<void>;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  setMenuOrder: (order: string[]) => Promise<void>;
  toggleMenuItemVisibility: (itemKey: string) => Promise<void>;
  resetMenuToDefault: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { profile, user } = useAuth();
  const [primaryColor, setPrimaryColorState] = useState('#9B6BFF');
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [menuOrder, setMenuOrderState] = useState<string[]>(DEFAULT_MENU_ORDER);
  const [hiddenMenuItems, setHiddenMenuItemsState] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load settings from profile
  useEffect(() => {
    if (profile) {
      setPrimaryColorState(profile.primary_color || '#9B6BFF');
      setThemeModeState(profile.theme_mode || 'light');
      setMenuOrderState(profile.menu_order || DEFAULT_MENU_ORDER);
      setHiddenMenuItemsState(profile.hidden_menu_items || []);
      setIsLoading(false);
    }
  }, [profile]);

  // Apply CSS variables when settings change
  useEffect(() => {
    const root = document.documentElement;

    // Primary color and variants
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--color-primary-light', adjustColor(primaryColor, 30));
    root.style.setProperty('--color-primary-lighter', adjustColor(primaryColor, 60));
    root.style.setProperty('--color-primary-dark', adjustColor(primaryColor, -20));

    // RGB values for opacity usage
    const rgb = hexToRgb(primaryColor);
    if (rgb) {
      root.style.setProperty('--color-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }

    // Theme mode — .dark for Tailwind, data-theme for butter.css
    if (themeMode === 'dark') {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.removeAttribute('data-theme');
    }
  }, [primaryColor, themeMode]);

  // Save to database
  const saveSettings = async (updates: Partial<{
    primary_color: string;
    theme_mode: ThemeMode;
    menu_order: string[];
    hidden_menu_items: string[];
  }>) => {
    if (!user?.id) return;

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) {
      logger.error('Error saving settings:', error);
      throw error;
    }
  };

  const setPrimaryColor = async (color: string) => {
    setPrimaryColorState(color);
    await saveSettings({ primary_color: color });
  };

  const setThemeMode = async (mode: ThemeMode) => {
    setThemeModeState(mode);
    await saveSettings({ theme_mode: mode });
  };

  const setMenuOrder = async (order: string[]) => {
    setMenuOrderState(order);
    await saveSettings({ menu_order: order });
  };

  const toggleMenuItemVisibility = async (itemKey: string) => {
    const newHidden = hiddenMenuItems.includes(itemKey)
      ? hiddenMenuItems.filter((k) => k !== itemKey)
      : [...hiddenMenuItems, itemKey];
    setHiddenMenuItemsState(newHidden);
    await saveSettings({ hidden_menu_items: newHidden });
  };

  const resetMenuToDefault = async () => {
    setMenuOrderState(DEFAULT_MENU_ORDER);
    setHiddenMenuItemsState([]);
    await saveSettings({
      menu_order: DEFAULT_MENU_ORDER,
      hidden_menu_items: [],
    });
  };

  return (
    <SettingsContext.Provider
      value={{
        primaryColor,
        themeMode,
        menuOrder,
        hiddenMenuItems,
        isLoading,
        setPrimaryColor,
        setThemeMode,
        setMenuOrder,
        toggleMenuItemVisibility,
        resetMenuToDefault,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
